/**
 * BenchRoutingSection - B2B / large-order bench routing settings (BLU-197)
 *
 * Configures how orders are routed to a work bench instead of a bin at the
 * catcher station:
 * - Enable toggle (master gate — dark by default)
 * - Unit threshold: orders above N units route to a bench
 * - Bench-routing tags: Shopify tags that force a bench regardless of unit count
 * - B2B tags: Shopify tags that mark an order as B2B (also bench-routed)
 *
 * These map 1:1 to prod_settings columns the catcher's bench-routing module
 * already consumes: bench_routing_enabled, bench_unit_threshold,
 * bench_routing_tags, b2b_tags.
 *
 * Backed by `productionSettings.getBenchRoutingSettings` /
 * `updateBenchRoutingSettings`. Load + save + dirty-state mirror the sibling
 * order tag-filter card in ProductionSettingsPage (savedRef baseline, toast on
 * success/error, refetch to invalidate the get query).
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Plus, X, Info, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';

const DEFAULT_BENCH_UNIT_THRESHOLD = 15;
const MIN_BENCH_UNIT_THRESHOLD = 1;
const MAX_BENCH_UNIT_THRESHOLD = 1000;
const MAX_TAGS = 20;

interface TagInputProps {
  id: string;
  label: string;
  description: string;
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  placeholder: string;
  emptyHint: string;
  pillClassName: string;
  pillButtonClassName: string;
  disabled: boolean;
}

function TagInput({
  id,
  label,
  description,
  tags,
  onAdd,
  onRemove,
  placeholder,
  emptyHint,
  pillClassName,
  pillButtonClassName,
  disabled,
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const tag = draft.trim();
    if (tag && !tags.includes(tag) && tags.length < MAX_TAGS) {
      onAdd(tag);
      setDraft('');
    }
  };
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor={id} className="text-base font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground/90">{description}</p>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[44px] p-2 border rounded-md bg-muted/30">
        {tags.map((tag) => (
          <span key={tag} className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-sm font-medium ${pillClassName}`}>
            {tag}
            <button
              type="button"
              onClick={() => onRemove(tag)}
              disabled={disabled}
              className={`min-h-[32px] min-w-[32px] flex items-center justify-center rounded-full transition-colors disabled:opacity-50 ${pillButtonClassName}`}
              aria-label={`Remove ${tag} tag`}
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        ))}
        {tags.length === 0 && (
          <span className="text-sm text-muted-foreground italic">{emptyHint}</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          id={id}
          placeholder={placeholder}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          className="h-11 flex-1"
        />
        <Button
          variant="outline"
          onClick={commit}
          disabled={disabled || !draft.trim() || tags.length >= MAX_TAGS}
          className="h-11 gap-1"
        >
          <Plus className="h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}

interface BenchRoutingSnapshot {
  enabled: boolean;
  threshold: number;
  benchTags: string[];
  b2bTags: string[];
}

export function BenchRoutingSection() {
  const { toast } = useToast();

  // Editable form state.
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState<number>(DEFAULT_BENCH_UNIT_THRESHOLD);
  const [benchTags, setBenchTags] = useState<string[]>([]);
  const [b2bTags, setB2bTags] = useState<string[]>([]);

  // Dirty-state baseline (mirrors savedTagFiltersRef in ProductionSettingsPage).
  const [loaded, setLoaded] = useState(false);
  const savedRef = useRef<BenchRoutingSnapshot>({
    enabled: false,
    threshold: DEFAULT_BENCH_UNIT_THRESHOLD,
    benchTags: [],
    b2bTags: [],
  });

  const settingsQuery = trpc.productionSettings.getBenchRoutingSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateBenchRoutingSettings.useMutation();

  // Initialize form state from query data when it loads.
  useEffect(() => {
    if (settingsQuery.data && !loaded) {
      const snapshot: BenchRoutingSnapshot = {
        enabled: settingsQuery.data.benchRoutingEnabled,
        threshold: settingsQuery.data.benchUnitThreshold,
        benchTags: settingsQuery.data.benchRoutingTags,
        b2bTags: settingsQuery.data.b2bTags,
      };
      setEnabled(snapshot.enabled);
      setThreshold(snapshot.threshold);
      setBenchTags(snapshot.benchTags);
      setB2bTags(snapshot.b2bTags);
      savedRef.current = snapshot;
      setLoaded(true);
    }
  }, [settingsQuery.data, loaded]);

  // Threshold within backend bounds (int 1..1000) — empty input parses to NaN.
  const thresholdValid =
    Number.isInteger(threshold) &&
    threshold >= MIN_BENCH_UNIT_THRESHOLD &&
    threshold <= MAX_BENCH_UNIT_THRESHOLD;

  const saved = savedRef.current;
  const hasUnsavedChanges =
    enabled !== saved.enabled ||
    threshold !== saved.threshold ||
    JSON.stringify(benchTags) !== JSON.stringify(saved.benchTags) ||
    JSON.stringify(b2bTags) !== JSON.stringify(saved.b2bTags);

  const handleSave = () => {
    if (!thresholdValid) {
      toast({
        title: 'Invalid unit threshold',
        description: `Enter a whole number between ${MIN_BENCH_UNIT_THRESHOLD} and ${MAX_BENCH_UNIT_THRESHOLD}.`,
        variant: 'destructive',
        duration: 5000,
      });
      return;
    }
    updateMutation.mutate(
      {
        benchRoutingEnabled: enabled,
        benchUnitThreshold: threshold,
        benchRoutingTags: benchTags,
        b2bTags,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Bench routing saved',
            description: 'B2B and large-order bench routing settings have been updated',
            duration: 3000,
          });
          // Update saved ref so the dirty check reflects the new baseline.
          savedRef.current = { enabled, threshold, benchTags, b2bTags };
          settingsQuery.refetch();
        },
        onError: (error) => {
          toast({
            title: 'Failed to save',
            description: error.message,
            variant: 'destructive',
            duration: 5000,
          });
        },
      }
    );
  };

  // Controls are inert until the current settings have loaded.
  const controlsDisabled = !loaded || updateMutation.isPending;
  const saveDisabled =
    controlsDisabled || !hasUnsavedChanges || !thresholdValid;

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading bench routing settings…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Enable toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <Label htmlFor="bench-routing-enabled" className="text-base font-medium">
            Bench routing
          </Label>
          <p className="text-sm text-muted-foreground/90">
            {enabled ? (
              <>Bench routing is <span className="font-medium text-green-600">on</span>. B2B and large orders are sent to a work bench instead of a bin.</>
            ) : (
              <>Bench routing is <span className="font-medium text-amber-600">off</span>. All orders use bins as usual.</>
            )}
          </p>
        </div>
        <Switch
          id="bench-routing-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={controlsDisabled}
          className="shrink-0"
        />
      </div>

      <Separator />

      {/* Unit threshold */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <Label htmlFor="bench-unit-threshold" className="text-base font-medium">
            Unit threshold
          </Label>
          <p className="text-sm text-muted-foreground/90">
            Orders with more than this many units route to a bench. Whole number from{' '}
            {MIN_BENCH_UNIT_THRESHOLD} to {MAX_BENCH_UNIT_THRESHOLD}.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Input
            id="bench-unit-threshold"
            type="number"
            inputMode="numeric"
            min={MIN_BENCH_UNIT_THRESHOLD}
            max={MAX_BENCH_UNIT_THRESHOLD}
            value={Number.isNaN(threshold) ? '' : threshold}
            disabled={controlsDisabled}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setThreshold(Number.isNaN(v) ? NaN : v);
            }}
            className={`w-full sm:w-[120px] h-11 text-center text-lg font-medium tabular-nums ${
              thresholdValid ? '' : 'border-destructive focus-visible:ring-destructive'
            }`}
            aria-invalid={!thresholdValid}
            aria-describedby="bench-unit-threshold-hint"
          />
          {!thresholdValid && (
            <p id="bench-unit-threshold-hint" className="text-xs text-destructive">
              Enter a whole number {MIN_BENCH_UNIT_THRESHOLD}-{MAX_BENCH_UNIT_THRESHOLD}.
            </p>
          )}
        </div>
      </div>

      <Separator />

      {/* Bench-routing tags */}
      <TagInput
        id="bench-routing-tags"
        label="Bench-routing tags"
        description="Orders with any of these Shopify tags route to a bench, regardless of unit count."
        tags={benchTags}
        onAdd={(tag) => setBenchTags([...benchTags, tag])}
        onRemove={(tag) => setBenchTags(benchTags.filter((x) => x !== tag))}
        placeholder="Add tag (e.g., bench, oversized)"
        emptyHint="No bench-routing tags"
        pillClassName="bg-indigo-100 text-indigo-800"
        pillButtonClassName="hover:bg-indigo-200"
        disabled={controlsDisabled}
      />

      <Separator />

      {/* B2B tags */}
      <TagInput
        id="b2b-tags"
        label="B2B tags"
        description="Orders with any of these Shopify tags are treated as B2B and routed to a bench."
        tags={b2bTags}
        onAdd={(tag) => setB2bTags([...b2bTags, tag])}
        onRemove={(tag) => setB2bTags(b2bTags.filter((x) => x !== tag))}
        placeholder="Add tag (e.g., wholesale, b2b)"
        emptyHint="No B2B tags"
        pillClassName="bg-sky-100 text-sky-800"
        pillButtonClassName="hover:bg-sky-200"
        disabled={controlsDisabled}
      />

      <Separator />

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saveDisabled}
          className="h-12 min-w-[160px] gap-2"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Saving…
            </>
          ) : (
            'Save bench routing'
          )}
        </Button>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <strong>Benches</strong> are created and labelled (BNCH-001…) in the{' '}
              <Link
                to="/production/benches/manage"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                bench manager
              </Link>
              . Each routed order is assigned to a bench at the catcher station, then packed from it.
            </p>
            <p>
              Default unit threshold is {DEFAULT_BENCH_UNIT_THRESHOLD}. Tags are matched against the
              order's Shopify tags.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
